/**
 * @fileoverview Generic modal content component for rendering modal content with a caption, text, and navigation buttons
 *
 * @example
 * // Usage in component:
 * import { GenericModalContent } from './GenericModalContent'
 *
 * <GenericModalContent caption="Test Caption" text="Test Text" confirmText="Confirm" cancelText="Cancel" onCancel={onCancel} onConfirm={onConfirm} />
 */

import ReactMarkdown from 'react-markdown'
import { Icon } from '../Icon'
import { NavigationButton } from '../NavigationButton'
import { BoldCaption } from '../typography/Typography'
import css from './Modal.module.css'

/**
 * @interface PromptModalProps
 * @description Props for the GenericModalContent component
 * @property {string} caption - The caption for the modal
 * @property {string} text - The text for the modal
 * @property {string} confirmText - The text for the confirm button
 * @property {string} cancelText - The text for the cancel button
 * @property {() => Promise<void>} onCancel - The function to call when the cancel button is clicked
 * @property {() => Promise<void>} onConfirm - The function to call when the confirm button is clicked
 */
interface PromptModalProps {
    caption: string
    text?: string
    confirmText?: string
    cancelText?: string
    onCancel?: () => Promise<void>
    onConfirm?: () => Promise<void>
}

/**
 * @function GenericModalContent
 * @description Component for rendering generic modal content with a caption, text, and navigation buttons
 * @param {PromptModalProps} props - The props for the component
 * @returns {JSX.Element} The GenericModalContent component
 */
export function GenericModalContent(props: PromptModalProps) {
    return (
        <div className={css.generic_error_content}>
            <div className={css.generic_header}>
                <Icon name={'satisfied2'} />
                <BoldCaption className={css.modal_caption}>{props.caption}</BoldCaption>
            </div>

            <div className={css.markdown}>
                <ReactMarkdown>{props.text}</ReactMarkdown>
            </div>

            <div className={css.button_container}>
                <NavigationButton type="cancel" label={props.cancelText} onClick={props.onCancel} />
                <NavigationButton
                    type="confirm"
                    label={props.confirmText}
                    onClick={props.onConfirm}
                />
            </div>
        </div>
    )
}
